import {
  PublicClientApplication,
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import { appConfig } from '../config';

/**
 * MSAL is configured with localStorage token caching so shared store accounts
 * stay signed in across browser restarts on store devices. Audit DATA never
 * touches localStorage (IndexedDB only, per spec §8) — this cache holds only
 * MSAL's own tokens.
 *
 * Lazily constructed so importing this module has no browser side effects
 * (unit tests import the Graph layer in a Node environment).
 */
let instance: PublicClientApplication | null = null;

export function getMsalInstance(): PublicClientApplication {
  if (!instance) {
    instance = new PublicClientApplication({
      auth: {
        clientId: appConfig.clientId,
        authority: appConfig.authority,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'localStorage',
      },
    });
  }
  return instance;
}

export function getActiveAccount(): AccountInfo | null {
  const msal = getMsalInstance();
  const active = msal.getActiveAccount();
  if (active) return active;
  const all = msal.getAllAccounts();
  const first = all[0];
  if (first) {
    msal.setActiveAccount(first);
    return first;
  }
  return null;
}

let redirectPending = false;

/**
 * Silent-renewal failures that an interactive redirect fixes. Beyond the
 * explicit interaction-required signal, MSAL's hidden-iframe renewal (used
 * once the refresh token ages out, ~daily) fails with these BrowserAuthError
 * codes whenever the browser blocks third-party cookies — routine in Chrome
 * and Edge now, not an outage. Treat them the same: sign in via redirect.
 */
const IFRAME_FAILURE_CODES = ['monitor_window_timeout', 'block_iframe_reload'];

function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  return err instanceof BrowserAuthError && IFRAME_FAILURE_CODES.includes(err.errorCode);
}

/**
 * Acquire a Graph access token, silently when possible, falling back to a
 * redirect when interaction is required (expired refresh token, revocation,
 * conditional-access change).
 *
 * `extraScopes` supports incremental consent: features needing permissions
 * beyond the base set (e.g. directory search) request them per-call, so
 * ordinary sign-in never depends on those permissions being consented. Note
 * extra-scope failures throw instead of redirecting — a missing optional
 * permission must degrade the feature, not bounce the user through Entra.
 */
export async function acquireGraphToken(
  extraScopes: readonly string[] = [],
): Promise<string> {
  // Extra-scope failures throw instead of redirecting (see above).
  return acquireToken(
    [...appConfig.graphScopes, ...extraScopes],
    extraScopes.length === 0,
  );
}

/**
 * Token for the app's own server API (/api, Phase 6.5). A separate
 * resource from Graph, so MSAL caches it separately; admin consent is
 * granted once in Entra (entra-setup §8.3), so users never see a prompt.
 */
export async function acquireApiToken(): Promise<string> {
  if (!appConfig.apiScope) throw new Error('VITE_ENTRA_API_SCOPE is not set');
  try {
    return await acquireToken([appConfig.apiScope], true, isMissingConsent);
  } catch (err) {
    if (isMissingConsent(err)) {
      throw new Error(
        'AuditPlus can’t reach its server yet — ask IT to finish its setup.',
      );
    }
    throw err;
  }
}

/**
 * Missing admin consent is a setup gap, not an expired session: sending
 * the user to Entra would only bring them back to the same error — a
 * redirect loop on every page load. Fail with a message instead.
 */
function isMissingConsent(err: unknown): boolean {
  if (!(err instanceof InteractionRequiredAuthError)) return false;
  return (
    err.errorCode === 'consent_required' ||
    err.subError === 'consent_required' ||
    err.errorMessage.includes('AADSTS65001')
  );
}

async function acquireToken(
  scopes: string[],
  redirectOnInteraction: boolean,
  neverRedirect: (err: unknown) => boolean = () => false,
): Promise<string> {
  const account = getActiveAccount();
  if (!account) {
    throw new Error('No signed-in account');
  }
  const msal = getMsalInstance();
  try {
    const result = await msal.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch (err) {
    if (needsInteraction(err)) {
      if (!redirectOnInteraction || neverRedirect(err)) throw err;
      // Concurrent fetches can all land here at once; only the first may
      // start the redirect (a second acquireTokenRedirect throws
      // interaction_in_progress). Everyone then parks on a promise that
      // never settles — the page is navigating away — so callers don't
      // flash transient errors while the browser unloads.
      if (!redirectPending) {
        redirectPending = true;
        await msal.acquireTokenRedirect({ scopes, account });
      }
      return new Promise<never>(() => {});
    }
    throw err;
  }
}
